import type { UserLog } from "@/types/UserLog";

/**
 * W4-d — the bell rows that announce a new request, and where a click on one
 * goes. Kept free of React so the mapping is a plain unit test.
 */
export const REQUEST_NOTIFICATION_TYPES = new Set([
  "GUEST_REQUEST_SUBMITTED",
  "COMPANION_REQUEST_SUBMITTED",
  "APPOINTMENT_REQUEST_SUBMITTED",
]);

export function isRequestNotification(type: string): boolean {
  return REQUEST_NOTIFICATION_TYPES.has(type);
}

const OBJECT_ID_HEX = /^[0-9a-f]{24}$/i;

/**
 * The page a request row opens, or null when the row cannot be followed (not
 * a request row, the event is gone, no event id on the row).
 *
 *  - GUEST      → the requests page, "register" tab, the guest focused
 *  - COMPANION  → the requests page, "companions" tab, the companion focused
 *  - APPOINTMENT→ the programme (slotting) page ONLY. The request surface is
 *                 `programme/offering/:offeringId?requestId=`, and the row
 *                 carries no `offeringId` (backend follow-up: put it on the
 *                 APPOINTMENT_REQUEST_SUBMITTED DTO next to `subjectId`).
 *                 Until then the link cannot reach the request itself — so
 *                 the click does NOT mark the row read (`requestOpenMarksRead`),
 *                 and no `tab`/`focus` params are sent that the page ignores.
 *
 * `focus` is the row's `subjectId` — the guest or appointment-request id — and
 * is omitted once the backend has no subject any more (erased guest), so the
 * link still lands on the right tab and just highlights nothing.
 */
export function requestHref(
  notification: Pick<UserLog, "type" | "subjectId" | "subjectKind" | "place" | "placeDeleted">,
): string | null {
  if (!isRequestNotification(notification.type)) return null;
  if (notification.placeDeleted === true) return null;
  const eventId = (notification.place as { eventId?: string } | undefined)?.eventId;
  if (!eventId || !OBJECT_ID_HEX.test(eventId)) return null;
  const focus =
    notification.subjectId && OBJECT_ID_HEX.test(notification.subjectId)
      ? `&focus=${notification.subjectId}`
      : "";
  switch (notification.type) {
    case "GUEST_REQUEST_SUBMITTED":
      return `/event/${eventId}/guests/requests?tab=register${focus}`;
    case "COMPANION_REQUEST_SUBMITTED":
      return `/event/${eventId}/guests/requests?tab=companions${focus}`;
    case "APPOINTMENT_REQUEST_SUBMITTED":
      return `/event/${eventId}/programme`;
    default:
      return null;
  }
}

/**
 * Whether following a request row may mark it read. True when the link lands
 * ON the request (guest / companion rows); false for an appointment row,
 * whose link only reaches the programme page today (see `requestHref`) — a
 * request the organiser never saw must not turn "read" on that click.
 */
export function requestOpenMarksRead(type: string): boolean {
  return isRequestNotification(type) && type !== "APPOINTMENT_REQUEST_SUBMITTED";
}
